import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, inject, Output } from '@angular/core';
import { Calendar, CircleChevronLeft, CircleChevronRight, LucideAngularModule } from 'lucide-angular';
import { DateFormattingService } from '../../../core/services/date-formatting.service';
import { SharedCardComponent } from '../shared-card/shared-card.component';

export interface DayNavigationItem {
  day: number;
  date: Date;
  label: string;
}

@Component({
  selector: 'app-day-navigation',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, SharedCardComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './day-navigation.component.html',
})
export class DayNavigationComponent {
  @Input() dayItems: DayNavigationItem[] = [];
  @Input() currentDay: number = 1;
  @Output() daySelected = new EventEmitter<number>();

  readonly CircleChevronLeft = CircleChevronLeft;
  readonly CircleChevronRight = CircleChevronRight;
  readonly Calendar = Calendar;

  private dateFormattingService = inject(DateFormattingService);

  get currentDayItem(): DayNavigationItem | undefined {
    return this.dayItems.find((item) => item.day === this.currentDay);
  }

  get currentDayDateFormatted(): string {
    if (!this.currentDayItem?.date) return '';

    return this.dateFormattingService.formatDateCompact(this.currentDayItem.date, true);
  }

  get canNavigatePrevious(): boolean {
    const dayNumbers = this.dayItems.map((item) => item.day).sort((a, b) => a - b);
    const currentIndex = dayNumbers.indexOf(this.currentDay);
    return currentIndex > 0;
  }

  get canNavigateNext(): boolean {
    const dayNumbers = this.dayItems.map((item) => item.day).sort((a, b) => a - b);
    const currentIndex = dayNumbers.indexOf(this.currentDay);
    return currentIndex < dayNumbers.length - 1;
  }

  selectDay(day: number): void {
    if (day !== this.currentDay) {
      this.daySelected.emit(day);
    }
  }

  previousDay(): void {
    if (this.canNavigatePrevious) {
      const dayNumbers = this.dayItems.map((item) => item.day).sort((a, b) => a - b);
      const currentIndex = dayNumbers.indexOf(this.currentDay);
      if (currentIndex > 0) {
        this.selectDay(dayNumbers[currentIndex - 1]);
      }
    }
  }

  nextDay(): void {
    if (this.canNavigateNext) {
      const dayNumbers = this.dayItems.map((item) => item.day).sort((a, b) => a - b);
      const currentIndex = dayNumbers.indexOf(this.currentDay);
      if (currentIndex < dayNumbers.length - 1) {
        this.selectDay(dayNumbers[currentIndex + 1]);
      }
    }
  }
}
